import { useMemo } from 'react';

import { useChatState } from '@ui-furniture/common/chat-ui/hooks/use-chat-state';
import { useChatStateWithDelays } from '@ui-furniture/common/chat-ui/hooks/use-chat-state-with-delays';
import { MementoService } from '@ui-furniture/common/memento/memento-service';

import { QuestionnaireRunner } from './questionnaire-runner';
import { createCommonTools } from './tools/create-common-tools';
import { createDataStoreTools } from './tools/create-data-store-tools';
import { createWebChatTools } from './tools/create-web-chat-tools';
import { GetDataFromQuestionnaire } from './types/get-data-from-questionnaire';
import { WebChatQuestionnaire } from './web-chat-questionnaire';

import { useAnalyticsReportingService } from '../analytics';

export const useQuestionnaireWithWebChat = <
  TQuestionnaire extends WebChatQuestionnaire<any, any>
>(
  questionnaire: TQuestionnaire,
  {
    initialData,
    onDone,
  }: {
    initialData: GetDataFromQuestionnaire<TQuestionnaire>;
    onDone?: (
      data: GetDataFromQuestionnaire<TQuestionnaire>
    ) => Promise<boolean>;
  }
) => {
  const mementoService = useMemo(() => new MementoService(), []);

  let chatState = useChatState({
    mementoService,
  });
  chatState = useChatStateWithDelays(chatState);
  const { reportEvent } = useAnalyticsReportingService();

  const start = useMemo(() => {
    const {
      createMemento: createMementoForDataStore,
      dataStore,
      ...dataTools
    } = createDataStoreTools(initialData);
    const chatTools = createWebChatTools(chatState);
    const commonTools = createCommonTools();

    const questionList = questionnaire.withTools({
      ...chatTools,
      ...commonTools,
      ...dataTools,
      reportAnswer: (data) => () =>
        reportEvent({
          name: 'Questionnaire Answer',
          ...data,
        }),
    });
    const runner = new QuestionnaireRunner(questionList, {
      onDone: () => onDone?.(dataStore.getData()),
    });

    mementoService.registerMementoCreators([
      createMementoForDataStore,
      runner.createMemento,
    ]);

    return runner.start;
  }, []);

  return {
    chatState,
    start,
  };
};
